/**
 * Phase C — Sales Cooperative view.
 *
 * Shows each cooperative contract with:
 *  - Group target progress (kg matured / target / %)
 *  - Rebate tiers status + reward %
 *  - Member breakdown table with per-customer contribution kg
 *  - Product target progress with drill-down per member
 *
 * For INDIVIDUAL_CUSTOMER contracts, the group target is derived from the
 * "best" member (leader), but the full per-member breakdown is displayed so
 * the SALES operator can see who is falling behind.
 */
import React, { useEffect, useState } from "react";
import { Section, Card, Badge, Button, Alert } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

export default function SalesCooperativesPage() {
  const { apiGet } = useAuth();
  const [coops, setCoops] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [co, ct, cust] = await Promise.all([
          apiGet("/api/admin/cooperatives?active=true"),
          apiGet("/api/admin/contracts?owner_type=COOPERATIVE&active=true"),
          apiGet("/api/customers"),
        ]);
        setCoops(co); setContracts(ct); setCustomers(cust);
      } catch (e) { setErr(e?.message || String(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const customerById = Object.fromEntries((customers || []).map(c => [c.id, c]));

  return (
    <Section title="Cooperative · vista SALES"
             subtitle="Target di gruppo, avanzamento membri, rebate maturato"
             data-testid="sales-coop-page">
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      {!coops && <p className="muted">Caricamento…</p>}
      {coops && coops.length === 0 && (
        <Alert tone="info">Nessuna cooperativa attiva configurata.</Alert>
      )}
      {coops && coops.map((co) => {
        const myContracts = contracts.filter(c => c.cooperative_id === co.id);
        return (
          <Card key={co.id} style={{ marginBottom: 16 }}
                data-testid={`sales-coop-card-${co.code}`}>
            <Card.Header>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong>🏛️ {co.name}</strong>
                  <code style={{ marginLeft: 8, fontSize: 12, color: "#64748b" }}>{co.code}</code>
                </div>
                <Badge tone={co.is_active ? "success" : "neutral"}>
                  {co.is_active ? "Attiva" : "Inattiva"}
                </Badge>
              </div>
              {co.description && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{co.description}</div>}
            </Card.Header>
            <Card.Body>
              {myContracts.length === 0 && (
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Nessun contratto cooperativa attivo per questo gruppo.
                </div>
              )}
              {myContracts.map(ct => (
                <CooperativeContractCard key={ct.id} contract={ct}
                  customerById={customerById} />
              ))}
            </Card.Body>
          </Card>
        );
      })}
    </Section>
  );
}


function CooperativeContractCard({ contract, customerById }) {
  const { apiGet } = useAuth();
  const [prog, setProg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { setProg(await apiGet(`/api/admin/contracts/${contract.id}/progress`)); }
      catch (e) { setErr(e?.message || String(e)); }
    })();
    /* eslint-disable-next-line */
  }, [contract.id]);

  if (err) return <Alert variant="danger">Errore progress: {err}</Alert>;
  if (!prog) return <p className="muted">Caricamento avanzamento…</p>;

  const isGroup = prog.aggregation_mode === "GROUP_AGGREGATED";

  return (
    <div style={{ padding: 12, marginBottom: 12,
                  border: "1px solid #e2e8f0", borderRadius: 6,
                  background: "#F8FAFC" }}
         data-testid={`sales-coop-contract-${contract.code}`}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        <div>
          <code>{contract.code}</code> · <strong>{contract.name}</strong>
          <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>
            {contract.starts_at} → {contract.ends_at}
          </span>
        </div>
        <Badge tone={isGroup ? "info" : "warning"}>
          {isGroup ? "GROUP_AGGREGATED — target unico" : "INDIVIDUAL_CUSTOMER — target per membro"}
        </Badge>
      </div>

      <div style={{ padding: 8, background: "#E0F2FE", borderRadius: 4, marginBottom: 8 }}>
        <strong>PFA {isGroup ? "gruppo" : "leader membro"}: </strong>
        <span data-testid={`sales-coop-total-${contract.code}`}>{prog.customer_pfa_maturato_kg} kg</span>
        <span style={{ marginLeft: 8, fontSize: 12, color: "#0369A1" }}>
          (fino a {prog.period_evaluated_to})
        </span>
      </div>

      <h5 style={{ margin: "8px 0 4px" }}>Rebate tiers ({prog.rebate_tiers.length})</h5>
      {prog.rebate_tiers.map((r, i) => (
        <div key={i} style={{ padding: 6, background: r.reached ? "#DCFCE7" : "white",
                              border: "1px solid #E5E7EB", borderRadius: 4, marginBottom: 4, fontSize: 12 }}
             data-testid={`sales-coop-tier-${contract.code}-${i}`}>
          <div>
            <strong>{r.threshold_kg} kg</strong> → {r.reward_percent}%{" "}
            <Badge tone={r.aggregation_mode === "GROUP" ? "info" : "warning"}>
              {r.aggregation_mode}
            </Badge>{" "}
            {r.reached ? "✅ Raggiunto" : `❌ mancano ${r.missing_kg} kg`}
          </div>
          {r.member_breakdown && Object.keys(r.member_breakdown).length > 0 && (
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: "pointer", color: "#0369A1", fontSize: 11 }}>
                Contributo per membro
              </summary>
              <table className="ds-table" style={{ marginTop: 4, fontSize: 11 }}>
                <thead><tr><th>Cliente</th><th style={{ textAlign: "right" }}>kg contribuiti</th></tr></thead>
                <tbody>
                  {Object.entries(r.member_breakdown)
                    .sort((a, b) => Number(b[1]) - Number(a[1]))
                    .map(([cid, kg]) => (
                      <tr key={cid} data-testid={`sales-coop-member-${cid}`}>
                        <td>{customerById[cid]?.business_name || cid.slice(0, 8)}</td>
                        <td style={{ textAlign: "right" }}>{kg}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      ))}

      {prog.product_targets.length > 0 && (
        <>
          <h5 style={{ margin: "10px 0 4px" }}>Target articolo ({prog.product_targets.length})</h5>
          {prog.product_targets.map((t, i) => (
            <div key={i} style={{ padding: 6, background: t.reached ? "#DCFCE7" : "white",
                                  border: "1px solid #E5E7EB", borderRadius: 4, marginBottom: 4, fontSize: 12 }}>
              <div><strong>{t.product_sku}</strong> — {t.product_name}
                <Badge tone={t.aggregation_mode === "GROUP" ? "info" : "warning"}
                       style={{ marginLeft: 6 }}>{t.aggregation_mode}</Badge>
              </div>
              <div style={{ color: "#475569" }}>
                Target: {t.target_kg} kg · Maturato: <strong>{t.maturato_kg} kg</strong>{" "}
                ({t.percent_reached}%) · Reward {t.reward_percent}%
              </div>
            </div>
          ))}
        </>
      )}

      {prog.members && prog.members.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", color: "#0369A1", fontSize: 12 }}>
            👥 Membri correnti ({prog.members.length}) — contributo PFA
          </summary>
          <table className="ds-table" style={{ marginTop: 6, fontSize: 12 }}
                 data-testid={`sales-coop-members-${contract.code}`}>
            <thead><tr><th>Cliente</th><th style={{ textAlign: "right" }}>kg maturati</th></tr></thead>
            <tbody>
              {prog.members
                .slice()
                .sort((a, b) => Number(b.matured_kg) - Number(a.matured_kg))
                .map(m => (
                  <tr key={m.customer_id}>
                    <td>{customerById[m.customer_id]?.business_name || m.customer_id.slice(0, 8)}</td>
                    <td style={{ textAlign: "right" }}>{m.matured_kg}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
